import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import styles from './LoginPage.module.css'
import { useAuthStore } from '../../features/auth'
import { routes } from '../../shared/config/routes'
import LoginLogo from '../../app/assets/svgs/logo.svg'
import { FieldWrapper } from '../../shared/ui'
import { Button } from '../../shared/ui/controls/Button/Button'
import { FormInput } from '../../shared/ui/controls/FormInput'
import { getErrorMessage } from '../../shared/helpers/getErrorMessage'

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email ist erforderlich')
    .regex(/^\S+@\S+\.\S+$/, 'Bitte eine gültige Email eingeben'),
  password: z
    .string()
    .min(1, 'Passwort ist erforderlich')
    .min(6, 'Mindestens 6 Zeichen'),
})

type LoginFormValues = z.infer<typeof loginSchema>

const LoginPage = () => {
  const login = useAuthStore((s) => s.login)
  const authError = useAuthStore((s) => s.authError)
  const logoutReason = useAuthStore((s) => s.logoutReason)
  const clearLogoutReason = useAuthStore((s) => s.clearLogoutReason)

  useEffect(() => () => { clearLogoutReason(); }, [clearLogoutReason])

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = handleSubmit(async ({ email, password }) => {
    clearErrors('root')

    try {
      await login(email, password)
    } catch (err) {
      setError('root', {
        type: 'server',
        message: getErrorMessage(err),
      })
    }
  })

  const disableSubmit = isSubmitting

  return (
    <div className={styles.page}>
      <div className={styles.main}>
        <img src={LoginLogo} alt="login Logo" />

        <form onSubmit={onSubmit} noValidate className='!w-[300px] flex flex-col gap-[20px]'>
          <FieldWrapper label='Email'>
            <FormInput
              name="email"
              control={control}
              autoComplete="email"
              type="email"
              placeholder="Email eingeben"
              clearable
            />
          </FieldWrapper>

          <FieldWrapper label='Passwort'>
            <FormInput
              name="password"
              control={control}
              autoComplete="current-password"
              type="password"
              placeholder="Passwort eingeben"
              onEnter={onSubmit}
            />
          </FieldWrapper>

          {logoutReason === 'archived' && (
            <div className={styles.error} role="alert">
              Ihr Konto wurde archiviert. Bitte wenden Sie sich an einen Administrator.
            </div>
          )}

          {(errors.root?.message || authError) && (
            <div className={styles.error} role="alert">
              {errors.root?.message ?? 'Benutzername oder Passwort ist ungültig'}
            </div>
          )}

          <Button type="submit" disabled={disableSubmit}>
            {disableSubmit ? 'Anmelden…' : 'Anmelden'}
          </Button>

          <div className={styles.footer}>
            <Link to={routes.auth.forgotPassword}>Passwort vergessen?</Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LoginPage
